import { useState } from "react";
import { Check, User, FileText, MapPin, ClipboardCheck, ChevronRight, ChevronLeft, ShieldCheck, AlertCircle, Upload } from "lucide-react";

/* ---------------------------------------------------------
   Design tokens (inline, since arbitrary Tailwind values
   aren't compiled in this environment)
--------------------------------------------------------- */
const T = {
    navy: "#14213D",
    navySoft: "#243357",
    paper: "#F3F1EA",
    card: "#FFFFFF",
    border: "#E1DCCF",
    saffron: "#C97D2E",
    saffronSoft: "#F3E2CC",
    green: "#2F6B4F",
    greenSoft: "#E3EEE7",
    red: "#B3413B",
    redSoft: "#F6E4E2",
    slate: "#6B7280",
    ink: "#1F2430",
};

const SERIF = "'Source Serif 4', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Courier New', monospace";

/* ---------------------------------------------------------
   Shared field primitives
--------------------------------------------------------- */
function Field({ label, hint, error, children, mono }) {
    return (
        <div style={{ marginBottom: "20px" }}>
            <label
                style={{
                    display: "block",
                    fontFamily: SANS,
                    fontSize: "12.5px",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: T.navy,
                    marginBottom: "7px",
                }}
            >
                {label}
            </label>
            {children}
            {hint && !error && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: T.slate, marginTop: "6px" }}>
                    {hint}
                </div>
            )}
            {error && (
                <div
                    style={{
                        fontFamily: SANS,
                        fontSize: "12px",
                        color: T.red,
                        marginTop: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                    }}
                >
                    <AlertCircle size={13} /> {error}
                </div>
            )}
        </div>
    );
}

function inputStyle(error, mono) {
    return {
        width: "100%",
        boxSizing: "border-box",
        padding: "11px 13px",
        borderRadius: "6px",
        border: `1.5px solid ${error ? T.red : T.border}`,
        background: T.paper,
        fontFamily: mono ? MONO : SANS,
        fontSize: mono ? "14.5px" : "14.5px",
        letterSpacing: mono ? "0.04em" : "normal",
        color: T.ink,
        outline: "none",
    };
}

function TextInput(props) {
    const { error, mono, ...rest } = props;
    return <input {...rest} style={inputStyle(error, mono)} />;
}

function SelectInput({ error, children, ...rest }) {
    return (
        <select {...rest} style={{ ...inputStyle(error, false), appearance: "auto" }}>
            {children}
        </select>
    );
}

function FileUpload({ file, onChange, error, id, label }) {
    const fmtSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div>
            <input
                id={id}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => onChange(e.target.files?.[0] || null)}
                style={{ display: "none" }}
            />
            <label
                htmlFor={id}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    border: `1.5px dashed ${error ? T.red : file ? T.green : T.border}`,
                    borderRadius: "8px",
                    padding: "13px 15px",
                    background: file ? T.greenSoft : T.paper,
                    cursor: "pointer",
                }}
            >
                <div
                    style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "6px",
                        background: file ? "rgba(47,107,79,0.15)" : T.saffronSoft,
                        color: file ? T.green : T.saffron,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Upload size={15} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {file ? (
                        <>
                            <div style={{ fontFamily: SANS, fontSize: "13px", fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {file.name}
                            </div>
                            <div style={{ fontFamily: SANS, fontSize: "11.5px", color: T.slate }}>
                                {fmtSize(file.size)} · tap to replace
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontFamily: SANS, fontSize: "13px", fontWeight: 600, color: T.ink }}>
                                {label || "Upload a clear photo or scan"}
                            </div>
                            <div style={{ fontFamily: SANS, fontSize: "11.5px", color: T.slate }}>
                                JPG, PNG or PDF · up to 5 MB
                            </div>
                        </>
                    )}
                </div>
                {file && <Check size={16} color={T.green} strokeWidth={3} />}
            </label>
            {error && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: T.red, marginTop: "6px", display: "flex", alignItems: "center", gap: "5px" }}>
                    <AlertCircle size={13} /> {error}
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------
   STEP 1 — Personal Information
--------------------------------------------------------- */
function PersonalInfoStep({ data, update, errors }) {
    return (
        <div>
            <StepHeading
                eyebrow="Step 01"
                title="Personal information"
                desc="Tell us who you are, exactly as it appears on your government ID."
            />
            <Row>
                <Field label="Full name" error={errors.fullName}>
                    <TextInput
                        placeholder="e.g. Ananya Rao Deshmukh"
                        value={data.fullName}
                        onChange={(e) => update("fullName", e.target.value)}
                        error={errors.fullName}
                    />
                </Field>
                <Field label="Father's / Husband's name" error={errors.guardianName}>
                    <TextInput
                        placeholder="e.g. Ramesh Rao Deshmukh"
                        value={data.guardianName}
                        onChange={(e) => update("guardianName", e.target.value)}
                        error={errors.guardianName}
                    />
                </Field>
            </Row>
            <Row>
                <Field label="Date of birth" error={errors.dob}>
                    <TextInput
                        type="date"
                        value={data.dob}
                        onChange={(e) => update("dob", e.target.value)}
                        error={errors.dob}
                    />
                </Field>
                <Field label="Gender" error={errors.gender}>
                    <SelectInput
                        value={data.gender}
                        onChange={(e) => update("gender", e.target.value)}
                        error={errors.gender}
                    >
                        <option value="">Select</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="other">Other / prefer not to say</option>
                    </SelectInput>
                </Field>
            </Row>
            <Row>
                <Field label="Mobile number" error={errors.mobile} hint="10 digits, no country code">
                    <TextInput
                        mono
                        placeholder="98765 43210"
                        maxLength={10}
                        value={data.mobile}
                        onChange={(e) => update("mobile", e.target.value.replace(/\D/g, ""))}
                        error={errors.mobile}
                    />
                </Field>
                <Field label="Email address" error={errors.email}>
                    <TextInput
                        type="email"
                        placeholder="you@example.com"
                        value={data.email}
                        onChange={(e) => update("email", e.target.value)}
                        error={errors.email}
                    />
                </Field>
            </Row>
        </div>
    );
}

/* ---------------------------------------------------------
   STEP 2 — Documents (Aadhar, PAN, GST)
--------------------------------------------------------- */
function DocCard({ icon, title, subtitle, children, verified }) {
    return (
        <div
            style={{
                border: `1.5px solid ${T.border}`,
                borderRadius: "10px",
                padding: "18px 20px",
                marginBottom: "16px",
                background: T.card,
                position: "relative",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div
                        style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "7px",
                            background: T.saffronSoft,
                            color: T.saffron,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: MONO,
                            fontWeight: 700,
                            fontSize: "12px",
                        }}
                    >
                        {icon}
                    </div>
                    <div>
                        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: "14.5px", color: T.navy }}>{title}</div>
                        <div style={{ fontFamily: SANS, fontSize: "12px", color: T.slate }}>{subtitle}</div>
                    </div>
                </div>
                {verified && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontFamily: SANS,
                            fontSize: "11.5px",
                            fontWeight: 700,
                            color: T.green,
                            background: T.greenSoft,
                            padding: "4px 9px",
                            borderRadius: "999px",
                            letterSpacing: "0.03em",
                        }}
                    >
                        <ShieldCheck size={13} /> LOOKS VALID
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}

function DocumentsStep({ data, update, errors }) {
    const aadharValid = /^\d{12}$/.test(data.aadhar);
    const panValid = /^[A-Z]{5}\d{4}[A-Z]$/.test(data.pan);
    const gstValid = data.gst === "" || /^\d{2}[A-Z0-9]{13}$/.test(data.gst);

    const formatAadhar = (v) =>
        v
            .replace(/\D/g, "")
            .slice(0, 12)
            .replace(/(\d{4})(?=\d)/g, "$1 ");

    return (
        <div>
            <StepHeading
                eyebrow="Step 02"
                title="Identity documents"
                desc="Enter your Aadhar and PAN details. GST is only needed if you're registering a business."
            />

            <DocCard icon="AA" title="Aadhar number" subtitle="12-digit Unique Identification Number" verified={aadharValid}>
                <Field error={errors.aadhar} hint="Format: XXXX XXXX XXXX">
                    <TextInput
                        mono
                        placeholder="1234 5678 9012"
                        value={formatAadhar(data.aadhar)}
                        onChange={(e) => update("aadhar", e.target.value.replace(/\D/g, "").slice(0, 12))}
                        error={errors.aadhar}
                    />
                </Field>
                <Field label="Upload Aadhar card" error={errors.aadharFile}>
                    <FileUpload
                        id="aadharFile"
                        file={data.aadharFile}
                        onChange={(f) => update("aadharFile", f)}
                        error={errors.aadharFile}
                        label="Upload front side of Aadhar card"
                    />
                </Field>
            </DocCard>

            <DocCard icon="PAN" title="PAN number" subtitle="Permanent Account Number, for tax identification" verified={panValid}>
                <Field error={errors.pan} hint="Format: ABCDE1234F">
                    <TextInput
                        mono
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        value={data.pan}
                        onChange={(e) => update("pan", e.target.value.toUpperCase().slice(0, 10))}
                        error={errors.pan}
                    />
                </Field>
                <Field label="Upload PAN card" error={errors.panFile}>
                    <FileUpload
                        id="panFile"
                        file={data.panFile}
                        onChange={(f) => update("panFile", f)}
                        error={errors.panFile}
                        label="Upload front side of PAN card"
                    />
                </Field>
            </DocCard>

            <DocCard
                icon="GST"
                title="GST number"
                subtitle="Goods & Services Tax ID — optional, for businesses only"
                verified={data.gst.length > 0 && gstValid}
            >
                <Field error={errors.gst} hint="Format: 22ABCDE1234F1Z5 · leave blank if not applicable">
                    <TextInput
                        mono
                        placeholder="22ABCDE1234F1Z5"
                        maxLength={15}
                        value={data.gst}
                        onChange={(e) => update("gst", e.target.value.toUpperCase().slice(0, 15))}
                        error={errors.gst}
                    />
                </Field>
                <Field label="Upload GST certificate" hint="Optional">
                    <FileUpload
                        id="gstFile"
                        file={data.gstFile}
                        onChange={(f) => update("gstFile", f)}
                        label="Upload GST registration certificate"
                    />
                </Field>
            </DocCard>
        </div>
    );
}

/* ---------------------------------------------------------
   STEP 3 — Address Details
--------------------------------------------------------- */
function AddressStep({ data, update, errors }) {
    return (
        <div>
            <StepHeading
                eyebrow="Step 03"
                title="Address details"
                desc="This should match the address on your identity documents."
            />
            <Field label="Address line 1" error={errors.addr1}>
                <TextInput
                    placeholder="House / flat no., building, street"
                    value={data.addr1}
                    onChange={(e) => update("addr1", e.target.value)}
                    error={errors.addr1}
                />
            </Field>
            <Field label="Address line 2" hint="Optional">
                <TextInput
                    placeholder="Landmark, area"
                    value={data.addr2}
                    onChange={(e) => update("addr2", e.target.value)}
                />
            </Field>
            <Row>
                <Field label="City" error={errors.city}>
                    <TextInput
                        placeholder="e.g. Hyderabad"
                        value={data.city}
                        onChange={(e) => update("city", e.target.value)}
                        error={errors.city}
                    />
                </Field>
                <Field label="State" error={errors.state}>
                    <SelectInput value={data.state} onChange={(e) => update("state", e.target.value)} error={errors.state}>
                        <option value="">Select state</option>
                        {[
                            "Andhra Pradesh", "Delhi", "Gujarat", "Karnataka", "Kerala",
                            "Maharashtra", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana",
                            "Uttar Pradesh", "West Bengal", "Other",
                        ].map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </SelectInput>
                </Field>
            </Row>
            <Row>
                <Field label="PIN code" error={errors.pincode}>
                    <TextInput
                        mono
                        placeholder="500081"
                        maxLength={6}
                        value={data.pincode}
                        onChange={(e) => update("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                        error={errors.pincode}
                    />
                </Field>
                <Field label="Country">
                    <TextInput value="India" disabled style={{ ...inputStyle(false, false), opacity: 0.6 }} />
                </Field>
            </Row>
        </div>
    );
}

/* ---------------------------------------------------------
   STEP 4 — Preview & Submit
--------------------------------------------------------- */
function SummaryRow({ label, value }) {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "9px 0",
                borderBottom: `1px solid ${T.border}`,
                gap: "20px",
            }}
        >
            <span style={{ fontFamily: SANS, fontSize: "12.5px", color: T.slate, flexShrink: 0 }}>{label}</span>
            <span
                style={{
                    fontFamily: SANS,
                    fontSize: "13.5px",
                    color: T.ink,
                    fontWeight: 600,
                    textAlign: "right",
                }}
            >
                {value || "—"}
            </span>
        </div>
    );
}

function SummaryCard({ title, onEdit, children }) {
    return (
        <div
            style={{
                border: `1.5px solid ${T.border}`,
                borderRadius: "10px",
                background: T.card,
                padding: "18px 20px",
                marginBottom: "16px",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: "13.5px", color: T.navy, letterSpacing: "0.02em" }}>
                    {title}
                </span>
                <button
                    onClick={onEdit}
                    style={{
                        background: "none",
                        border: "none",
                        color: T.saffron,
                        fontFamily: SANS,
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        padding: 0,
                    }}
                >
                    Edit
                </button>
            </div>
            {children}
        </div>
    );
}

function PreviewStep({ data, goTo, agreed, setAgreed }) {
    return (
        <div>
            <StepHeading
                eyebrow="Step 04"
                title="Preview & submit"
                desc="Check every field carefully. Once submitted, changes require re-verification."
            />

            <SummaryCard title="PERSONAL INFORMATION" onEdit={() => goTo(0)}>
                <SummaryRow label="Full name" value={data.fullName} />
                <SummaryRow label="Father's / Husband's name" value={data.guardianName} />
                <SummaryRow label="Date of birth" value={data.dob} />
                <SummaryRow label="Gender" value={data.gender} />
                <SummaryRow label="Mobile" value={data.mobile} />
                <SummaryRow label="Email" value={data.email} />
            </SummaryCard>

            <SummaryCard title="DOCUMENTS" onEdit={() => goTo(1)}>
                <SummaryRow label="Aadhar number" value={data.aadhar && data.aadhar.replace(/(\d{4})(?=\d)/g, "$1 ")} />
                <SummaryRow label="Aadhar file" value={data.aadharFile?.name} />
                <SummaryRow label="PAN number" value={data.pan} />
                <SummaryRow label="PAN file" value={data.panFile?.name} />
                <SummaryRow label="GST number" value={data.gst} />
                <SummaryRow label="GST file" value={data.gstFile?.name} />
            </SummaryCard>

            <SummaryCard title="ADDRESS" onEdit={() => goTo(2)}>
                <SummaryRow label="Address" value={[data.addr1, data.addr2].filter(Boolean).join(", ")} />
                <SummaryRow label="City" value={data.city} />
                <SummaryRow label="State" value={data.state} />
                <SummaryRow label="PIN code" value={data.pincode} />
            </SummaryCard>

            <label
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    fontFamily: SANS,
                    fontSize: "12.5px",
                    color: T.slate,
                    marginTop: "6px",
                    cursor: "pointer",
                }}
            >
                <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    style={{ marginTop: "2px" }}
                />
                I confirm the details above are accurate and match my official documents.
            </label>
        </div>
    );
}

/* ---------------------------------------------------------
   Layout helpers
--------------------------------------------------------- */
function Row({ children }) {
    return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>{children}</div>;
}

function StepHeading({ eyebrow, title, desc }) {
    return (
        <div style={{ marginBottom: "26px" }}>
            <div
                style={{
                    fontFamily: SANS,
                    fontSize: "11.5px",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: T.saffron,
                    marginBottom: "6px",
                }}
            >
                {eyebrow}
            </div>
            <h2 style={{ fontFamily: SERIF, fontSize: "26px", color: T.navy, margin: "0 0 8px 0" }}>{title}</h2>
            <p style={{ fontFamily: SANS, fontSize: "13.5px", color: T.slate, margin: 0, lineHeight: 1.5 }}>{desc}</p>
        </div>
    );
}

/* ---------------------------------------------------------
   Stepper (signature element — official document seal)
--------------------------------------------------------- */
const STEPS = [
    { label: "Personal information", icon: User },
    { label: "Documents", icon: FileText },
    { label: "Address details", icon: MapPin },
    { label: "Preview & submit", icon: ClipboardCheck },
];

function Stepper({ current, furthest, goTo }) {
    return (
        <div style={{ padding: "26px 24px 30px", marginBottom: "22px", background: T.card, borderRadius: "12px", border: `1px solid ${T.border}` }}>

            <div style={{ display: "flex", alignItems: "flex-start" }}>
                {STEPS.map((step, i) => {
                    const state = i < current ? "done" : i === current ? "active" : "pending";
                    const clickable = i <= furthest;
                    const Icon = step.icon;
                    return (
                        <div key={step.label} style={{ display: "flex", alignItems: "flex-start", flex: i < STEPS.length - 1 ? 1 : "none" }}>
                            <div
                                onClick={() => clickable && goTo(i)}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "10px",
                                    cursor: clickable ? "pointer" : "default",
                                    minWidth: "90px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "38px",
                                        height: "38px",
                                        borderRadius: "50%",
                                        border: `1.5px dashed ${state === "pending" ? T.border : state === "active" ? T.saffron : T.green}`,
                                        background: T.card,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        transform: state === "active" ? "rotate(-6deg)" : "none",
                                    }}
                                >
                                    {state === "done" ? (
                                        <Check size={16} color={T.green} strokeWidth={3} />
                                    ) : (
                                        <Icon size={16} color={state === "active" ? T.saffron : T.slate} />
                                    )}
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <div
                                        style={{
                                            fontFamily: SANS,
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            letterSpacing: "0.08em",
                                            color: state === "active" ? T.saffron : T.slate,
                                        }}
                                    >
                                        STEP 0{i + 1}
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: SANS,
                                            fontSize: "12.5px",
                                            fontWeight: state === "active" ? 700 : 500,
                                            color: state === "pending" ? T.slate : T.navy,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {step.label}
                                    </div>
                                </div>
                            </div>

                            {i < STEPS.length - 1 && (
                                <div
                                    style={{
                                        flex: 1,
                                        height: "1.5px",
                                        marginTop: "19px",
                                        background: i < current ? T.green : T.border,
                                        minWidth: "24px",
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------
   Validation
--------------------------------------------------------- */
function validateStep(step, data) {
    const e = {};
    if (step === 0) {
        if (!data.fullName.trim()) e.fullName = "Enter your full name";
        if (!data.guardianName.trim()) e.guardianName = "Required";
        if (!data.dob) e.dob = "Select date of birth";
        if (!data.gender) e.gender = "Select an option";
        if (!/^\d{10}$/.test(data.mobile)) e.mobile = "Enter a valid 10-digit number";
        if (!/^\S+@\S+\.\S+$/.test(data.email)) e.email = "Enter a valid email";
    }
    if (step === 1) {
        if (!/^\d{12}$/.test(data.aadhar)) e.aadhar = "Aadhar must be exactly 12 digits";
        if (!data.aadharFile) e.aadharFile = "Upload a copy of your Aadhar card";
        if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(data.pan)) e.pan = "Enter a valid PAN, e.g. ABCDE1234F";
        if (!data.panFile) e.panFile = "Upload a copy of your PAN card";
        if (data.gst && !/^\d{2}[A-Z0-9]{13}$/.test(data.gst)) e.gst = "Enter a valid 15-character GSTIN";
    }
    if (step === 2) {
        if (!data.addr1.trim()) e.addr1 = "Required";
        if (!data.city.trim()) e.city = "Required";
        if (!data.state) e.state = "Select a state";
        if (!/^\d{6}$/.test(data.pincode)) e.pincode = "Enter a valid 6-digit PIN";
    }
    return e;
}

/* ---------------------------------------------------------
   Main page
--------------------------------------------------------- */
export default function RegistrationForm() {
    const [current, setCurrent] = useState(0);
    const [furthest, setFurthest] = useState(0);
    const [errors, setErrors] = useState({});
    const [agreed, setAgreed] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [data, setData] = useState({
        fullName: "", guardianName: "", dob: "", gender: "", mobile: "", email: "",
        aadhar: "", aadharFile: null, pan: "", panFile: null, gst: "", gstFile: null,
        addr1: "", addr2: "", city: "", state: "", pincode: "",
    });

    const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

    const goTo = (i) => {
        setErrors({});
        setCurrent(i);
    };

    const handleNext = () => {
        if (current < 3) {
            const e = validateStep(current, data);
            setErrors(e);
            if (Object.keys(e).length > 0) return;
            const next = current + 1;
            setCurrent(next);
            setFurthest((f) => Math.max(f, next));
        }
    };

    const handleBack = () => {
        setErrors({});
        setCurrent((c) => Math.max(0, c - 1));
    };

    const handleSubmit = () => {
        if (!agreed) return;
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div style={{ minHeight: "100vh", background: T.paper, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                <div style={{ textAlign: "center", maxWidth: "420px" }}>
                    <div
                        style={{
                            width: "68px", height: "68px", borderRadius: "50%",
                            border: `2.5px dashed ${T.green}`, background: T.greenSoft,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            margin: "0 auto 22px", transform: "rotate(-6deg)",
                        }}
                    >
                        <Check size={30} color={T.green} strokeWidth={3} />
                    </div>
                    <h1 style={{ fontFamily: SERIF, fontSize: "28px", color: T.navy, margin: "0 0 10px" }}>
                        Registration submitted
                    </h1>
                    <p style={{ fontFamily: SANS, fontSize: "14px", color: T.slate, lineHeight: 1.6, marginBottom: "26px" }}>
                        Thanks, {data.fullName.split(" ")[0] || "there"}. We've received your details and documents.
                        A confirmation will be sent to <strong style={{ color: T.ink }}>{data.email}</strong>.
                    </p>
                    <button
                        onClick={() => { setSubmitted(false); setCurrent(0); setFurthest(0); setAgreed(false); }}
                        style={{
                            fontFamily: SANS, fontWeight: 700, fontSize: "13.5px", color: "#fff",
                            background: T.navy, border: "none", borderRadius: "7px", padding: "12px 22px", cursor: "pointer",
                        }}
                    >
                        Start a new registration
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh", height: "auto", overflowY: "auto", background: T.paper, padding: "40px 20px 60px", fontFamily: SANS, boxSizing: "border-box" }}>
            <div style={{  margin: "0 auto" }}>
                <Stepper current={current} furthest={furthest} goTo={goTo} />

                <div
                    style={{
                        background: T.card,
                        borderRadius: "12px",
                        border: `1px solid ${T.border}`,
                        padding: "34px 34px 26px",
                    }}
                >
                    {current === 0 && <PersonalInfoStep data={data} update={update} errors={errors} />}
                    {current === 1 && <DocumentsStep data={data} update={update} errors={errors} />}
                    {current === 2 && <AddressStep data={data} update={update} errors={errors} />}
                    {current === 3 && (
                        <PreviewStep data={data} goTo={goTo} agreed={agreed} setAgreed={setAgreed} />
                    )}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "28px",
                            paddingTop: "22px",
                            borderTop: `1px solid ${T.border}`,
                        }}
                    >
                        <button
                            onClick={handleBack}
                            disabled={current === 0}
                            style={{
                                display: "flex", alignItems: "center", gap: "6px",
                                fontFamily: SANS, fontWeight: 700, fontSize: "13.5px",
                                color: current === 0 ? "#C7C2B4" : T.navy,
                                background: "none", border: "none",
                                cursor: current === 0 ? "default" : "pointer", padding: "10px 6px",
                            }}
                        >
                            <ChevronLeft size={16} /> Back
                        </button>

                        {current < 3 ? (
                            <button
                                onClick={handleNext}
                                style={{
                                    display: "flex", alignItems: "center", gap: "6px",
                                    fontFamily: SANS, fontWeight: 700, fontSize: "13.5px", color: "#fff",
                                    background: T.navy, border: "none", borderRadius: "7px",
                                    padding: "12px 22px", cursor: "pointer",
                                }}
                            >
                                Continue <ChevronRight size={16} />
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={!agreed}
                                style={{
                                    display: "flex", alignItems: "center", gap: "6px",
                                    fontFamily: SANS, fontWeight: 700, fontSize: "13.5px", color: "#fff",
                                    background: agreed ? T.saffron : "#D9CBB5",
                                    border: "none", borderRadius: "7px",
                                    padding: "12px 22px", cursor: agreed ? "pointer" : "default",
                                }}
                            >
                                Submit registration <Check size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}